import { msg } from "@lingui/core/macro";
import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { createMemo, Show } from "solid-js";

import { appRoute } from "./route";
import { AuthPlaceholder } from "@/components/auth-placeholder";
import { PageContainer, PageHeader } from "@fleet/ui";
import { useI18n } from "@/providers/i18n-provider";
import { ComposeForm } from "@/features/mail/components/compose-form";
import { useMailAccounts, useMailMessage } from "@/features/mail/hooks";
import { type ComposeSearch, validateComposeSearch } from "@/features/mail/route-search";

function ComposePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const search = useSearch({ from: "/_app/mail/compose" }) as () => ComposeSearch;

  const accounts = useMailAccounts();
  const accountId = createMemo(() => {
    const list = accounts.data ?? [];
    const req = search().acct;
    if (req && list.some((a) => a.id === req)) {
      return req;
    }
    return list[0]?.id;
  });

  const original = useMailMessage(
    () => accountId(),
    () => search().folder ?? "INBOX",
    () => (search().mode && search().mode !== "new" ? search().uid : undefined),
  );

  const done = () => navigate({ to: "/mail", search: { acct: accountId() } });

  return (
    <AuthPlaceholder
      title={t(msg`Compose`)}
      description={t(msg`Sign in to send mail from your connected accounts.`)}
    >
      <PageContainer maxWidth="3xl">
        <PageHeader title={t(msg`Compose`)} />
        <Show
          when={accountId()}
          fallback={
            <p class="text-sm text-muted-foreground">{t(msg`Connect a mail account first.`)}</p>
          }
        >
          {(id) => (
            <ComposeForm
              accountId={id()}
              mode={search().mode ?? "new"}
              original={original.data}
              onDone={done}
            />
          )}
        </Show>
      </PageContainer>
    </AuthPlaceholder>
  );
}

export const mailComposeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mail/compose",
  validateSearch: validateComposeSearch,
  component: ComposePage,
});
